import { EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Typography } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  ActionKind,
  ActionParamsButton,
  ActionParamsLink,
  BaseClickable,
} from 'components/ui';
import { routes } from 'config/routes';
import { FormListEntity } from 'entities/FormList';
import { FormStatus } from 'entities/common';
import { rootStore } from 'stores/RootStore';
import { FormListPageStore } from 'stores/local';
import { handleStopPropagation } from 'utils/inputEvents';

type SetupColumnProps = {
  entity: FormListEntity;
  store: FormListPageStore;
};

const SetupColumn: React.FC<SetupColumnProps> = ({ entity, store }) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const editAction: ActionParamsLink = React.useMemo(
    () => ({
      actionKind: ActionKind.link,
      to: routes.form.formSteps.createPath(entity.id),
    }),
    [entity]
  );

  const makeChanges: ActionParamsLink = React.useMemo(
    () => ({
      actionKind: ActionKind.link,
      to: routes.form.formReview.flat.createPath({ id: String(entity.id) }),
    }),
    [entity]
  );

  const wrapperAction: ActionParamsButton<any> = React.useMemo(
    () => ({
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: handleStopPropagation,
      },
    }),
    []
  );

  const deleteAction: ActionParamsButton<any> = React.useMemo(
    () => ({
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: () => store.removeForm(entity.id),
      },
    }),
    [store, entity]
  );

  const approveAction: ActionParamsButton<any> = React.useMemo(
    () => ({
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: () => store.approveForm(entity.id),
      },
    }),
    [store, entity]
  );

  const notificationAction: ActionParamsButton<any> = React.useMemo(
    () => ({
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: () => setSearchParams({ notification: String(entity.id), tab: 'send' }),
      },
    }),
    [entity.id, setSearchParams]
  );

  const menuItems = React.useMemo(() => {
    const baseItems = [
      {
        key: 'notifications',
        label: (
          <BaseClickable action={notificationAction} block>
            <Typography.Text>
              {t('formList.notifications.title')}
            </Typography.Text>
          </BaseClickable>
        ),
      },
      {
        key: 'delete',
        label: (
          <BaseClickable action={deleteAction} block>
            <Typography.Text>
              {t('common.delete')}
            </Typography.Text>
          </BaseClickable>
        ),
      },
    ];

    if (entity.status === FormStatus.FINISHED) {
      baseItems.push({
        key: 'edit',
        label: (
          <BaseClickable action={makeChanges} block>
            <Typography.Text>{t('formList.makeChanges')}</Typography.Text>
          </BaseClickable>
        ),
      });
    } else {
      baseItems.push({
        key: 'edit',
        label: (
          <BaseClickable action={editAction} block>
            <Typography.Text>{t('common.edit')}</Typography.Text>
          </BaseClickable>
        ),
      });
    }

    if (
      entity.status === FormStatus.FINISHED ||
      entity.status !== FormStatus.PUBLISHED
    ) {
      return baseItems;
    }

    return [
      {
        key: 'approve',
        label: (
          <BaseClickable action={approveAction} block>
            <Typography.Text>
              {t('formList.completeApproval')}
            </Typography.Text>
          </BaseClickable>
        ),
      },
      ...baseItems,
    ];
  }, [deleteAction, entity.status, approveAction, makeChanges, editAction, notificationAction, t]);

  if (!rootStore.userStore.user?.isCompanySuperuser) {
    return null;
  }

  return (
    <BaseClickable action={wrapperAction} withHover withCursorPointer>
      <Dropdown
        placement="bottomLeft"
        trigger={['hover', 'click']}
        overlay={<Menu items={menuItems}></Menu>}
      >
        <EllipsisOutlined />
      </Dropdown>
    </BaseClickable>
  );
};

export default React.memo(SetupColumn);
